import React, { useEffect, useState } from 'react';
import { History as HistoryIcon } from 'lucide-react';
import { Card } from '../components/ui';
import { PredictionList } from '../components/predictions';
import { useAuthStore, usePredictionStore, useUIStore } from '../store';
import { api } from '../lib/api';
import type { Prediction } from '../types';

export function History() {
  const { user } = useAuthStore();
  const { setSelectedPrediction } = usePredictionStore();
  const { setView } = useUIStore();

  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    const loadHistory = async () => {
      try {
        const response = await api.predictions.getMine();
        if (response.success && response.data) {
          setPredictions(response.data);
        }
      } catch (error) {
        console.error('Failed to load history:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadHistory();
  }, [user]);

  const stats = React.useMemo(() => {
    const resolved = predictions.filter((p) => p.status !== 'in-progress');
    const successful = resolved.filter((p) => p.status === 'success');
    const avgConfidence =
      predictions.length > 0
        ? predictions.reduce((sum, p) => sum + p.confidence, 0) / predictions.length
        : 0;

    return {
      total: predictions.length,
      resolved: resolved.length,
      successful: successful.length,
      successRate: resolved.length > 0 ? (successful.length / resolved.length) * 100 : 0,
      avgConfidence,
      inProgress: predictions.filter((p) => p.status === 'in-progress').length,
    };
  }, [predictions]);

  const handleSelectPrediction = (prediction: Prediction) => {
    setSelectedPrediction(prediction);
    setView('detail');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-foretell-accent" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="animate-in">
        <Card className="py-16 text-center">
          <HistoryIcon size={48} className="mx-auto mb-4 text-slate-500 opacity-50" />
          <p className="text-sm font-bold text-slate-500 tracking-wider uppercase">
            Sign in to view your history
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="animate-in">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-xl bg-foretell-accent/20 flex items-center justify-center text-foretell-accent">
          <HistoryIcon size={20} />
        </div>
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Your History</h2>
          <p className="text-sm text-slate-400">Track your prediction performance</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card padding="sm" className="text-center">
          <div className="text-2xl font-bold">{stats.total}</div>
          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
            Total
          </div>
        </Card>
        <Card padding="sm" className="text-center">
          <div className="text-2xl font-bold text-foretell-accent">{stats.inProgress}</div>
          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
            Active
          </div>
        </Card>
        <Card padding="sm" className="text-center">
          <div className="text-2xl font-bold text-emerald-400">
            {stats.successRate.toFixed(0)}%
          </div>
          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
            Success Rate
          </div>
        </Card>
        <Card padding="sm" className="text-center">
          <div className="text-2xl font-bold">{stats.avgConfidence.toFixed(0)}%</div>
          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
            Avg Confidence
          </div>
        </Card>
      </div>

      {stats.total > 0 && (
        <Card className="mb-8">
          <h3 className="text-sm font-bold mb-4">Performance Breakdown</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Successful</span>
              <div className="flex items-center gap-2">
                <div className="w-32 h-2 bg-black/40 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 rounded-full"
                    style={{
                      width: `${stats.resolved > 0 ? (stats.successful / stats.resolved) * 100 : 0}%`,
                    }}
                  />
                </div>
                <span className="text-sm font-bold text-emerald-400 w-8">
                  {stats.successful}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Failed</span>
              <div className="flex items-center gap-2">
                <div className="w-32 h-2 bg-black/40 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-rose-500 rounded-full"
                    style={{
                      width: `${
                        stats.resolved > 0
                          ? ((stats.resolved - stats.successful) / stats.resolved) * 100
                          : 0
                      }%`,
                    }}
                  />
                </div>
                <span className="text-sm font-bold text-rose-400 w-8">
                  {stats.resolved - stats.successful}
                </span>
              </div>
            </div>
          </div>
        </Card>
      )}

      <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">
        All Predictions
      </h3>

      <PredictionList
        predictions={predictions}
        onSelect={handleSelectPrediction}
        emptyMessage="You haven't made any predictions yet"
      />
    </div>
  );
}
